import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useApi } from "@/core/api";
import { dayjs } from "@/core/utils/date";
import type { IPackApprovalResponse } from "./types";

const PACK_APPROVAL_KEY = "pack-approval";

export const packApprovalKey = (
  businessId: string | null,
  runId: string | null,
) => [PACK_APPROVAL_KEY, businessId, runId] as const;

const path = (businessId: string, runId: string) =>
  `/businesses/${businessId}/ai_script_runs/${runId}/pack_approval`;

/**
 * When `value` last changed, as a timestamp; 0 until it has changed at least once.
 *
 * The first value to arrive is the baseline rather than a change, so a page that loads
 * the statement and the approval side by side does not refetch the approval for it.
 */
const useChangedAt = (value: unknown) => {
  const [tracked, setTracked] = useState({ value, at: 0 });
  if (tracked.value !== value) {
    const at = tracked.value === undefined ? 0 : dayjs().valueOf();
    setTracked({ value, at });
    return at;
  }
  return tracked.at;
};

/**
 * @param followsStatement The technician's statement as currently loaded. A published
 *   period is read live, so the figures can move under a standing approval; whenever
 *   the statement's data changes (react-query keeps the same reference while it does
 *   not), the approval is refetched and withheld until that answer arrives. Without it
 *   the approval's cache could say "You approved this pay" beside figures it has never
 *   been compared against.
 */
export const useGetPackApproval = (
  businessId: string | null,
  runId: string | null,
  enabled = true,
  { followsStatement }: { followsStatement?: unknown } = {},
) => {
  const api = useApi();
  const query = useQuery({
    queryKey: packApprovalKey(businessId, runId),
    queryFn: () => {
      if (!businessId || !runId) throw new Error("Payroll run is required");
      return api
        .get(path(businessId, runId))
        .then((res) => res.data as IPackApprovalResponse);
    },
    enabled: enabled && !!businessId && !!runId,
    // A business off the rollout 404s here. Retrying it would be three requests
    // for an answer that will not change.
    retry: false,
  });
  const statementChangedAt = useChangedAt(followsStatement);
  const behind = statementChangedAt > query.dataUpdatedAt;
  const { refetch, isFetching } = query;
  useEffect(() => {
    if (enabled && behind && !isFetching) void refetch();
  }, [enabled, behind, isFetching, refetch]);

  return behind ? { ...query, data: undefined } : query;
};

/**
 * Both writes seed the cache from the SERVER's response rather than refetching, so a
 * technician who taps approve sees the tick immediately rather than after a round
 * trip — and never from a payload assembled here, which is how withdraw once wrote a
 * response missing a field the panel read.
 */
export const useApprovePay = (
  businessId: string | null,
  runId: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!businessId || !runId) throw new Error("Payroll run is required");
      return api
        .post(path(businessId, runId))
        .then((res) => res.data as IPackApprovalResponse);
    },
    onSuccess: (data) =>
      queryClient.setQueryData<IPackApprovalResponse>(
        packApprovalKey(businessId, runId),
        data,
      ),
  });
};

export const useWithdrawPayApproval = (
  businessId: string | null,
  runId: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!businessId || !runId) throw new Error("Payroll run is required");
      return api
        .delete(path(businessId, runId))
        .then((res) => res.data as IPackApprovalResponse);
    },
    onSuccess: (data) =>
      queryClient.setQueryData<IPackApprovalResponse>(
        packApprovalKey(businessId, runId),
        data,
      ),
  });
};
